import React from 'react';
import { Box, Typography, Container, Grid, Button, Stack, Chip, alpha } from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DashboardMockup from '../ui/DashboardMockup';

const Home = () => {
  const { t, lang } = useLanguage();

  const heroFeatures = t('hero.features', { returnObjects: true }) || [];

  return (
    <Box
      component="section"
      sx={{
        pt: { xs: 15, md: 25 },
        pb: { xs: 10, md: 15 },
        background: 'linear-gradient(135deg, #fdfdfd 0%, #f4f7f9 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* 15y Exp: Abstract geometric background for depth */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.4,
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(6, 57, 125, 0.1) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Decorative Glows */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: lang === 'ar' ? 'auto' : '-10%',
          left: lang === 'ar' ? '-10%' : 'auto',
          width: '800px',
          height: '800px',
          background: 'radial-gradient(circle, rgba(6, 57, 125, 0.08) 0%, rgba(6, 57, 125, 0) 70%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
          zIndex: 0
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={10} alignItems="center">
          <Grid item xs={12} md={6} data-aos="fade-right">
            <Box>
              {/* Authority Label */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box sx={{ width: 40, height: 2, backgroundColor: '#DD8748' }} />
                <Typography
                  variant="overline"
                  sx={{
                    color: '#DD8748',
                    fontWeight: 800,
                    letterSpacing: 3,
                    fontSize: '0.85rem'
                  }}
                >
                  {lang === 'ar' ? 'خبراء التحول الرقمي' : lang === 'en' ? 'B2B DIGITAL EXPERTS' : 'EXPERTS DU DIGITAL B2B'}
                </Typography>
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontWeight: 900,
                  color: '#1D2558',
                  lineHeight: 1.1,
                  mb: 3,
                  fontSize: { xs: '2.8rem', sm: '3.5rem', md: '4.5rem' },
                  letterSpacing: -2,
                  textShadow: '0 10px 20px rgba(6, 57, 125, 0.05)'
                }}
              >
                {t('hero.title')}
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  fontSize: '1.25rem',
                  mb: 5,
                  maxWidth: '580px',
                  lineHeight: 1.7,
                  fontWeight: 400,
                  opacity: 0.9
                }}
              >
                {t('hero.subtitle')}
              </Typography>

              {/* Quick Value Propositions */}
              <Stack direction="row" spacing={3} sx={{ mb: 6 }} flexWrap="wrap">
                {Array.isArray(heroFeatures) && heroFeatures.map((feature, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#DD8748', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={700} color="primary">
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                <Button
                  variant="contained"
                  size="large"
                  disableElevation
                  endIcon={lang === 'ar' ? null : <ArrowForwardIcon />}
                  sx={{
                    py: 2.5,
                    px: 6,
                    borderRadius: '16px',
                    backgroundColor: '#DD8748',
                    fontSize: '1.1rem',
                    fontWeight: 800,
                    textTransform: 'none',
                    transition: '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#1D2558',
                      transform: 'translateY(-5px)',
                      boxShadow: `0 20px 40px ${alpha('#1D2558', 0.2)}`,
                    }
                  }}
                >
                  {t('hero.discover')}
                </Button>
                <Button
                  variant="text"
                  size="large"
                  startIcon={<PlayArrowIcon sx={{ fontSize: '2rem !important' }} />}
                  sx={{
                    py: 2,
                    px: 3,
                    color: '#1D2558',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#DD8748',
                      '& .MuiButton-startIcon': { color: '#DD8748' }
                    }
                  }}
                >
                  {t('hero.contactUs')}
                </Button>
              </Stack>

              {/* Proof Section */}
              <Box sx={{ mt: 8, display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box sx={{ display: 'flex', '-webkit-mask-image': 'linear-gradient(to right, black 80%, transparent)' }}>
                  {[1, 2, 3, 4, 5].map(i => (
                    <Typography key={i} sx={{ color: '#DD8748', fontSize: '1.5rem', mr: 0.5 }}>★</Typography>
                  ))}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', borderLeft: '2px solid #ddd', pl: 3 }}>
                  {t('hero.trust')}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} data-aos="zoom-in" data-aos-delay="300">
            <DashboardMockup />
          </Grid>
        </Grid>
      </Container>

      {/* Scroll Indicator */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 40,
          left: '50%',
          transform: 'translateX(-50%)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          opacity: 0.5
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 40,
            border: '2px solid #1D2558',
            borderRadius: 10,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 4,
              height: 8,
              backgroundColor: '#1D2558',
              borderRadius: 2,
              animation: 'scroll 2s infinite'
            },
            '@keyframes scroll': {
              '0%': { opacity: 1, top: 8 },
              '100%': { opacity: 0, top: 24 }
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default Home;
